import dayjs from 'dayjs';
import { cloneDeep } from 'lodash';
import {
  mapActions,
  mapMutations,
  mapState,
} from 'vuex';

import { OrderList, OrderSearch } from '@/components/Order';
import { SET_DEPARTMENT_LIST } from '@/store/orders/constants';
import { GET_DEPARTMENT_LIST } from '@/store/setting/constants';
import bus from '@/utils/bus';
import {
  DEFAULT_PER_PAGE,
  FIRST_PAGE,
  ORDER_STATUS,
  ORDER_TYPE,
  ORDER_STATUS_WITH_TYPE_TEXT,
  PER_PAGE_OPTIONS,
  SNACKBAR_TYPES,
} from '@/utils/constants';

export default {
  name: 'Order',
  components: {
    OrderList,
    OrderSearch,
  },
  props: {
    action: {
      type: String,
      default: 'list',
    },
  },
  data() {
    return {
      currentTab: ORDER_STATUS_WITH_TYPE_TEXT.ONGOING,
      dates: [],
      dateType: 'start',
      orders: [],
      page: FIRST_PAGE,
      perPage: DEFAULT_PER_PAGE,
      search: '',
      sortField: 'receive',
      sortOrder: 'asc',
    };
  },
  computed: {
    ...mapState('orders', {
      gettingOrders: ({ gettingOrders }) => gettingOrders,
      ordersInfos: ({ orders }) => orders,
    }),
    allOrders() {
      return this.ordersInfos?.data?.list || [];
    },
    isValidQueryTab() {
      return Object.values(ORDER_STATUS_WITH_TYPE_TEXT).includes(this.queryTab);
    },
    total() {
      return this.pagination?.total || 0;
    },
    loading() {
      return this.gettingOrders;
    },
    pagination() {
      return this.ordersInfos?.pagination || {};
    },
    perPageOptions() {
      return PER_PAGE_OPTIONS;
    },
    queryTab() {
      const { query: { tab } = {} } = this.$route || {};
      return tab;
    },
    tabValue() {
      const obj = {
        [ORDER_STATUS_WITH_TYPE_TEXT.ONGOING]: {
          status: ORDER_STATUS.ONGOING,
          type: ORDER_TYPE.NORMAL,
        },
        [ORDER_STATUS_WITH_TYPE_TEXT.SCHEDULED]: {
          status: ORDER_STATUS.SCHEDULED,
          type: ORDER_TYPE.NORMAL,
        },
        [ORDER_STATUS_WITH_TYPE_TEXT.ENDED]: {
          status: ORDER_STATUS.ENDED,
          type: ORDER_TYPE.NORMAL,
        },
        [ORDER_STATUS_WITH_TYPE_TEXT.COMPLETED]: {
          type: ORDER_TYPE.COMPLETED,
        },
        [ORDER_STATUS_WITH_TYPE_TEXT.CANCELLED]: {
          type: ORDER_TYPE.CANCELLED,
        },
        [ORDER_STATUS_WITH_TYPE_TEXT.DRAFT]: {
          type: ORDER_TYPE.DRAFT,
        },
      };
      return obj[this.currentTab] || obj[ORDER_STATUS_WITH_TYPE_TEXT.ONGOING];
    },
    tabs() {
      return [
        {
          label: '已公告',
          total: this.ordersInfos?.data?.schedule_count || 0,
          value: ORDER_STATUS_WITH_TYPE_TEXT.SCHEDULED,
        },
        {
          label: '開放訂購中',
          total: this.ordersInfos?.data?.ongoing_count || 0,
          value: ORDER_STATUS_WITH_TYPE_TEXT.ONGOING,
        },
        {
          label: '已截止訂購',
          total: this.ordersInfos?.data?.ended_count || 0,
          value: ORDER_STATUS_WITH_TYPE_TEXT.ENDED,
        },
        {
          label: '已完成',
          value: ORDER_STATUS_WITH_TYPE_TEXT.COMPLETED,
        },
        {
          label: '已取消',
          value: ORDER_STATUS_WITH_TYPE_TEXT.CANCELLED,
        },
        {
          label: '草稿',
          total: this.ordersInfos?.data?.draft_count || 0,
          value: ORDER_STATUS_WITH_TYPE_TEXT.DRAFT,
        },
      ];
    },
  },
  beforeDestroy() {
    Object.assign(this.$data, this.$options.data.apply(this));
  },
  async created() {
    bus.$on('GET_ORDERS', async () => {
      await this.setPageOrGetData();
    });

    bus.$on('SET_ORDER', (order) => {
      const index = this.orders.findIndex(({ id }) => id === order?.id);
      if (index !== -1) {
        const copy = [...this.orders];
        copy[index] = order;
        this.orders = copy;
      }
    });

    if (this.isValidQueryTab) {
      this.currentTab = this.queryTab;
    }

    await this.loadOrders();
  },
  destroyed() {
    bus.$off('GET_ORDERS');
  },
  async mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    this.loadDepartmentInfo();
  },
  watch: {
    allOrders: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.orders = cloneDeep(newVal);
        }
      },
    },
    currentTab(newVal, oldVal) {
      const isValidAndNewTab = this.isValidQueryTab && newVal !== this.queryTab;
      if ((!this.queryTab && newVal !== oldVal) || isValidAndNewTab) {
        this.resetOrdersData();
        this.loadOrders();

        if (!this.queryTab || this.isValidQueryTab) {
          this.$router.push({ query: { tab: newVal } });
        }
      }
    },
    page() {
      this.loadOrders();
    },
    perPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.page === FIRST_PAGE) {
          this.loadOrders();
          return;
        }
        this.page = FIRST_PAGE;
      }
    },
  },
  methods: {
    ...mapActions('orders', {
      getOrders: 'GET_ORDERS',
    }),
    ...mapActions('setting', {
      getDepartmentList: GET_DEPARTMENT_LIST,
    }),
    ...mapMutations('orders', {
      setDepartmentList: SET_DEPARTMENT_LIST,
    }),
    async loadDepartmentInfo() {
      try {
        const departments = await this.getDepartmentList();
        this.setDepartmentList(departments);
      } catch (error) {
        const { message: msg = '' } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '讀取部門時時發生錯誤',
          msg,
        });
      }
    },
    async loadOrders() {
      try {
        const [startDate, endDate] = this.dates;
        const query = {
          date_query_type: this.dateType,
          end_time: endDate ? dayjs(endDate).endOf('day').format('YYYY-MM-DD HH:mm') : '',
          keyword: this.search,
          order: this.sortOrder,
          page: this.page,
          per_page: this.perPage,
          sort_by: this.sortField,
          start_time: startDate ? dayjs(startDate).startOf('day').format('YYYY-MM-DD HH:mm') : '',
          ...this.tabValue,
        };

        await this.getOrders(query);
      } catch (error) {
        const { message: msg = '' } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '讀取團購時發生錯誤',
          msg,
        });
      }
    },
    onClearSearch() {
      this.search = '';
      this.dates = [];
      this.setPageOrGetData();
    },
    onCreateNew() {
      this.$router.push({
        path: '/order/create',
        query: {
          tab: this.currentTab,
        },
      });
    },
    onShowScheduleList() {
      this.$router.push({
        path: '/order/schedule',
        query: {
          tab: this.currentTab,
        },
      });
    },
    onDatesUpdates(dates) {
      this.dates = dates;
    },
    onDateTypeUpdated(type) {
      this.dateType = type;
    },
    onSearch() {
      this.setPageOrGetData();
    },
    onSort(field, order) {
      this.sortField = field;
      this.sortOrder = order;
      this.setPageOrGetData();
    },
    resetOrdersData() {
      this.orders = [];
      this.page = FIRST_PAGE;
      this.perPage = DEFAULT_PER_PAGE;
    },
    setPageOrGetData() {
      this.orders = [];
      if (this.page !== FIRST_PAGE) {
        this.page = FIRST_PAGE;
      } else {
        this.loadOrders();
      }
    },
  },
};
